import type { PaymentActivity } from "../../../../domain/models/PaymentActivity";

export interface HistoryChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface PaymentHistoryLine {
  id: string;
  user: string;
  date: string;
  time: string;
  changes: HistoryChange[];
}

type MapOptions = {
  fallbackUser: string;
  trackedFields: readonly string[];
};

const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Europe/Paris",
});

const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Paris",
});

const resolveUserName = (
  activity: PaymentActivity,
  fallbackUser: string,
): string => {
  const causer = activity.causer;
  if (!causer) {
    return fallbackUser;
  }

  const fullName = `${causer.firstname ?? ""} ${causer.lastname ?? ""}`.trim();

  return fullName || causer.email || fallbackUser;
};

export const mapPaymentActivities = (
  activities: PaymentActivity[] | undefined,
  { fallbackUser, trackedFields }: MapOptions,
): PaymentHistoryLine[] => {
  if (!activities || activities.length === 0) {
    return [];
  }

  return [...activities]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    .map((activity) => {
      const old = activity.properties?.old ?? {};
      const attributes = activity.properties?.attributes ?? {};

      const changes: HistoryChange[] = trackedFields
        .filter((field) => field in attributes || field in old)
        .map((field) => ({
          field,
          from: old[field],
          to: attributes[field],
        }));

      const createdAt = new Date(activity.created_at);

      return {
        id: String(activity.id),
        user: resolveUserName(activity, fallbackUser),
        date: dateFormatter.format(createdAt),
        time: timeFormatter.format(createdAt),
        changes,
      };
    })
    .filter((line) => line.changes.length > 0);
};
